import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../store/useStore";
import WishlistButton from "./WishlistButton";
import { FaShoppingCart } from "react-icons/fa";

/**
 * ProductCard — reusable premium product card.
 * Props:
 *   product  — normalized product object from store
 *   compact  — boolean, smaller card for carousels
 */
const ProductCard = ({ product, compact = false }) => {
  const { addToCart, isInWishlist } = useStore();
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const [adding, setAdding] = useState(false);

  if (!product) return null;

  const hasDiscount = product.discount > 0;
  const price = parseFloat(product.price || 0).toFixed(0);
  const originalPrice = parseFloat(product.originalPrice || 0).toFixed(0);
  const productId = product.id ?? product.product_id;

  const handleCardClick = () => navigate(`/product/${productId}`);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (adding) return; // guard: ignore rapid clicks while request is in-flight
    setAdding(true);
    try {
      await addToCart(product, 1);
      // Use a stable toast ID so repeated quick clicks never stack multiple toasts
      toast.success(`${product.name} added to cart!`, {
        icon: <FaShoppingCart />,
        id: `add-cart-${productId}`,
      });
    } finally {
      setAdding(false);
    }
  };

  /* Buy Now — opens the product detail page so the user can select a variant and add to cart */
  const handleBuyNow = (e) => {
    e.stopPropagation();
    navigate(`/product/${productId}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-primary-100 hover:shadow-xl ${
        compact ? "min-w-[200px] max-w-[200px] sm:min-w-0 sm:max-w-none" : ""
      }`}
    >
      {/* ── Image area ── */}
      <div className="relative overflow-hidden bg-gradient-to-b from-primary-50/60 to-white">
        {/* Discount badge */}
        {hasDiscount && (
          <div className="absolute left-3 top-3 z-10 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-2.5 py-1 text-[10px] font-bold text-white shadow-md sm:text-xs">
            {product.discount}% OFF
          </div>
        )}

        {/* Wishlist button — always visible on mobile, fades in on hover on desktop */}
        <div
          className={`absolute right-3 top-3 z-10 transition-opacity duration-200 sm:${
            isInWishlist(productId)
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          } ${
            isInWishlist(productId)
              ? "opacity-100"
              : "opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
          }`}
        >
          <WishlistButton productId={productId} size="sm" />
        </div>

        {/* Product image — full width, square */}
        <div className="aspect-square w-full overflow-hidden p-4">
          {product.image && !imgErr ? (
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
              onError={() => setImgErr(true)}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-gray-200">
              <svg
                className="h-14 w-14"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xs font-medium text-gray-300">
                No Image
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Info area ── */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-gray-900 group-hover:text-primary-700 sm:text-base">
          {product.name}
        </h3>

        <div className="mt-auto flex items-end gap-2 pt-1">
          <span className="text-lg font-black text-primary-600 sm:text-xl">
            ₹{price}
          </span>
          {product.unit && (
            <span className="mb-0.5 text-xs text-gray-400">
              / {product.unit}
            </span>
          )}
          {hasDiscount && originalPrice !== price && (
            <span className="mb-0.5 ml-auto text-xs text-gray-400 line-through">
              ₹{originalPrice}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 rounded-full ${product.inStock !== false ? "bg-emerald-500" : "bg-red-400"}`}
          />
          <span
            className={`text-xs font-semibold ${product.inStock !== false ? "text-emerald-600" : "text-red-500"}`}
          >
            {product.inStock !== false ? "In Stock" : "Out of Stock"}
          </span>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            onClick={handleAddToCart}
            disabled={adding || product.inStock === false}
            className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-primary-500 bg-white py-2 text-xs font-bold text-primary-600 transition-all hover:bg-primary-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          >
            {adding ? (
              <svg
                className="h-3.5 w-3.5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            ) : (
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            )}
            {adding ? "" : "Cart"}
          </button>
          <button
            onClick={handleBuyNow}
            disabled={product.inStock === false}
            className="flex items-center justify-center rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-2 text-xs font-bold text-white shadow-sm transition-all hover:from-primary-700 hover:to-primary-800 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
          >
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
